import { useEffect, useRef, useState, type DependencyList } from 'react';
import { ApiError } from './api';

export interface Loaded<T> {
  data: T | null;
  error: string;
  loading: boolean;
}

/** Load once per change of `deps`; a stale response never overwrites a newer one. */
export function useLoad<T>(fn: () => Promise<T>, deps: DependencyList): Loaded<T> {
  const [state, set] = useState<Loaded<T>>({ data: null, error: '', loading: true });
  // The deps the current answer belongs to. On the render where they change, the effect has not
  // run yet: say loading then too, so the last answer never passes for this one for a frame.
  const answered = useRef<DependencyList | null>(null);
  useEffect(() => {
    let live = true;
    set((s) => ({ ...s, loading: true, error: '' }));
    fn()
      .then((data) => {
        if (live) {
          answered.current = deps;
          set({ data, error: '', loading: false });
        }
      })
      .catch((e) => {
        if (live) {
          answered.current = deps;
          set({
            data: null,
            error: e instanceof ApiError ? e.message : 'Could not reach the server',
            loading: false,
          });
        }
      });
    return () => {
      live = false;
    };
  }, deps);
  const current =
    answered.current !== null &&
    answered.current.length === deps.length &&
    answered.current.every((d, i) => Object.is(d, deps[i]));
  return current ? state : { ...state, loading: true };
}

/** Whether a media query matches, kept up to date: for layouts that change shape, not just style. */
export function useMedia(query: string): boolean {
  const get = () => typeof window !== 'undefined' && !!window.matchMedia?.(query).matches;
  const [on, setOn] = useState(get);
  useEffect(() => {
    const m = window.matchMedia?.(query);
    if (!m) return;
    const change = () => setOn(m.matches);
    change();
    m.addEventListener('change', change);
    return () => m.removeEventListener('change', change);
  }, [query]);
  return on;
}
